import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';

type Mode = 'light' | 'dark' | 'system';

type ThemeModeContextType = {
  mode: Mode;
  resolvedMode: 'light' | 'dark';
  setMode: (m: Mode) => void;
  toggleMode: () => void;
};

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(undefined);

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [mode, setMode] = useState<Mode>(() => {
    const saved = localStorage.getItem('theme-mode') as Mode | null;
    return saved || 'system';
  });

  const resolvedMode: 'light' | 'dark' = useMemo(() => {
    if (mode === 'system') return prefersDark ? 'dark' : 'light';
    return mode;
  }, [mode, prefersDark]);

  useEffect(() => {
    // persist user choice (including 'system')
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  useEffect(() => {
    // write to DOM for Tailwind CSS variables
    const root = document.documentElement;
    if (mode === 'system') {
      root.removeAttribute('data-theme');
      root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
    } else {
      root.setAttribute('data-theme', mode);
    }
  }, [mode, prefersDark]);

  const toggleMode = () => setMode(resolvedMode === 'dark' ? 'light' : 'dark');

  const value = useMemo(
    () => ({ mode, resolvedMode, setMode, toggleMode }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, resolvedMode]
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

export const useThemeMode = () => {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeModeProvider');
  return ctx;
};








